import { useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useGetClientSecret } from '../../queries'
import { buildPrepareRegisterPayload } from '../utils'
import { amplitudeEvent } from 'lib/amplitude'
import { groupEventRegistrationEventProps } from '../../registration-experiment'
import { useRegistrationInvite } from '../../registration-invite'

/**
 * Shared Review & Pay behavior (payload, client secret, analytics, submit).
 * Both the original and the redesigned Review & Pay steps render from this so
 * the two cannot drift apart.
 */
const useReviewAndPay = ({
  groupEvent,
  handleNext,
  paymentRequired,
  shouldFetchClientSecret,
  setShouldFetchClientSecret,
  shouldShowWaiver,
  stripePaymentFormRef,
}) => {
  const form = useFormContext()
  const { watch } = form
  const participants = useMemo(() => watch('participants') || [], [watch])
  const contact = useMemo(() => watch('contact') || [], [watch])
  const useFirstParticipantContactInformation = watch(
    'useFirstParticipantContactInformation',
  )
  const phoneNumber = useFirstParticipantContactInformation
    ? participants[0]?.phoneNumber
    : contact.phoneNumber

  const payloadKey = JSON.stringify({
    useFirstParticipantContactInformation,
    contact: {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phoneNumber: contact.phoneNumber,
    },
    participants: participants.map(p => ({
      firstName: p.firstName,
      lastName: p.lastName,
      email: p.email,
      phoneNumber: p.phoneNumber,
      type: p.type,
    })),
  })
  const {
    participants: participantsAttributes,
    contact: contactAttributes,
  } = useMemo(
    () =>
      buildPrepareRegisterPayload({
        participants,
        contact,
        useFirstParticipantContactInformation,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payloadKey],
  )

  const { token: inviteToken } = useRegistrationInvite()
  const { clientSecret, loading, error, success, message } = useGetClientSecret(
    {
      groupEventId: groupEvent.id,
      participants: participantsAttributes,
      contact: contactAttributes,
      invitationToken: inviteToken,
      enabled: shouldFetchClientSecret,
    },
  )

  useEffect(() => {
    if (clientSecret && shouldFetchClientSecret && setShouldFetchClientSecret) {
      setShouldFetchClientSecret(false)
    }
  }, [clientSecret, shouldFetchClientSecret, setShouldFetchClientSecret])

  useEffect(() => {
    amplitudeEvent('view-checkout-screen', {
      payment_required: paymentRequired,
      payment_intent_enabled: true,
      ...groupEventRegistrationEventProps({ groupEvent }),
    })
  }, [paymentRequired, groupEvent])

  const handleClick = async () => {
    if (shouldShowWaiver) {
      if (stripePaymentFormRef.current?.validatePayment) {
        const isValid = await stripePaymentFormRef.current.validatePayment()
        if (isValid) {
          handleNext({ fieldsToValidate: [] })
        }
      }
    } else {
      if (stripePaymentFormRef.current) {
        stripePaymentFormRef.current.submitPayment()
      }
    }
  }

  return {
    participants,
    phoneNumber,
    contactAttributes,
    clientSecret,
    loading,
    error,
    success,
    message,
    handleClick,
  }
}

export default useReviewAndPay
